<template>
    <div>
        <h1 class="display-heading">Payout Details</h1>

        <div class="mb-3">
            <h5>Partner Details</h5>
            <table class="table table-bordered">
                <colgroup>
                    <col width="300">
                    <col>
                </colgroup>
                <tr>
                    <th>Partner Type</th>
                    <td>
                        {{ partner.type ? partner.type : '-' }}
                    </td>
                </tr>
                <tr>
                    <th>Partner Name</th>
                    <td>
                        {{ partner.name ? partner.name : '-' }}
                    </td>
                </tr>
                <tr>
                    <th>Bank Name</th>
                    <td>
                        {{ partner.bankName ? partner.bankName : '-' }}
                    </td>
                </tr>
                <tr>
                    <th>Bank Account Payee Name</th>
                    <td>
                        {{ partner.bankPayeeName ? partner.bankPayeeName : '-' }}
                    </td>
                </tr>
                <tr>
                    <th>Bank Account Number</th>
                    <td>
                        {{ partner.bankAccountNumber ? partner.bankAccountNumber : '-' }}
                    </td>
                </tr>
            </table>
            <h5>
                Transaction Details ({{transactions.length}})
            </h5>
            <table class="table table-bordered">
                <colgroup>
                    <col width="50">
                    <col width="250">
                    <col width="200">
                    <col>
                    <col width="150">
                </colgroup>
                <thead>
                    <tr>
                       <th>
                           #
                       </th>
                       <th>
                           Transaction Date & Time
                       </th>
                       <th>Payment ID</th>
                       <th>
                           Transaction Details
                       </th>
                       <th class="text-right">
                           Disburseable <br> Amount (RM)
                       </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="transactions.length===0">
                        <td class="font-italic text-center" colspan="5">
                            No Data
                        </td>
                    </tr>
                     <tr v-for="(txn, index) in transactions" :key="index">
                        <td>
                            {{index+1}}
                        </td>
                        <td>
                            {{txn.completedDate}}
                        </td>
                        <td>
                            {{txn.paymentIdDisplay}}
                        </td>
                        <td v-html="txn.detail">
                        </td>
                        <td class="text-right">
                            {{txn.disbursementAmount.toFixed(2)}}
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th colspan="4" class="text-right"> Total Disburseable Amount (RM)</th>
                        <th class="text-right">{{ totalDisburseableAmount.toFixed(2) }}</th>
                    </tr>
                </tfoot>
            </table>
        </div>

        <div class="card mb-3">
            <div class="card-header">Disburse Payout</div>
            <div class="card-body">
                <div>
                    <p>Click here to set all transactions in this page as disbursed.</p>
                    <p>
                        <b>
                        Disbursement Amount: RM {{totalDisburseableAmount.toFixed(2)}}
                        </b>
                    </p>

                    <button class="btn btn-primary btn-primary-insv" @click="disbursePayout">
                        Disburse Payout
                    </button>
                </div>
            </div>
        </div>

        <div>
            <router-link :to="{'name': 'admin-partner-payout-v2'}" class="btn btn-primary btn-primary-insv">
                Back to Payout List
            </router-link>
        </div>

        <popup-modal ref="popupModalConfirmation" :hasCloseButton="false" title="Disburse Payout">
            <template slot="content">
                <template v-if="disbursement.done">
                    Success! Transactions has been set to disbursed!
                </template>
                <template v-else>
                    <p>Are you sure you want to disburse all the transactions for this partner?</p>
                    <p>
                        <b>
                        Disbursement Amount: RM {{totalDisburseableAmount.toFixed(2)}}
                        </b>
                    </p>
                    <p>This action cannot be undone!</p>
                    <p v-if="disbursement.isDisbursing" :class="{'text-danger': disbursement.isDisburseError}">
                        {{disbursement.disburseMessage}}
                    </p>

                </template>
            </template>
            <template slot="footer">
                <template v-if="!disbursement.done">
                    <button type="button" class="btn btn-primary btn-primary-insv" @click="confirmDisbursement">Confirm Disbursement</button>
                    <button class="btn btn-secondary" @click="dismissConfirm">Cancel</button>
                </template>
                <template v-else>
                    <button type="button" class="btn btn-primary btn-primary-insv" @click="dismissAndBackToList">Okay</button>
                </template>
            </template>
        </popup-modal>
    </div>
</template>

<script>
import popupModal from '@/components/popup-modal';
import { convertToUserReadableDateTime } from '@/utilities';
export default {
    'name': 'PayoutDetails',
    'components': {
        popupModal
    },
    'computed': {
        role () {
            return this.$route.params.role;
        },
        roleId () {
            return this.$route.params.roleId;
        }
    },
    data () {
        return {
            'partner': {
                'name': '',
                'type': '',
                'bankName': '',
                'bankPayeeName': '',
                'bankAccountNumber': ''
            },
            'transactions': [],
            'disbursement': {
                'isDisbursing': false,
                'isDisburseError': false,
                'disburseMessage': '',
                'done': false
            },
            'totalDisburseableAmount': 0

        };
    },
    mounted () {
        this.getPayoutDetails();
    },
    'methods': {
        getPayoutDetails () {
            this.axios.get('PaymentList/GetPayoutRecordV2', {
                'params': {
                    'partnerRole': this.role,
                    'partnerId': this.roleId
                }
            }).then(({ data }) => {
                if (data.data) {
                    const details = data.data;

                    if (details.p) {
                        const partner = details.p;

                        this.partner.name = partner.n;
                        this.partner.type = partner.rn;
                        this.partner.bankName = partner.bn;
                        this.partner.bankPayeeName = partner.bp;
                        this.partner.bankAccountNumber = partner.ba;
                    }

                    if (details.tl) {
                        this.transactions = details.tl.map((txn) => ({
                            'paymentIdDisplay': txn.psids,
                            'paymentId': txn.psid,
                            'completedDate': txn.rd ? convertToUserReadableDateTime(txn.rd) : '-',
                            'detail': txn.td,
                            'disbursementAmount': txn.da
                        }));
                    }

                    if (details.tt) {
                        this.totalDisburseableAmount = details.tt;
                    }
                }
            });
        },
        disbursePayout () {
            this.$refs.popupModalConfirmation.show();
        },
        confirmDisbursement () {
            this.disbursement.isDisburseError = false;
            this.disbursement.isDisbursing = true;
            this.disbursement.disburseMessage = 'Updating disbursement status...';
            this.axios.post('PaymentList/DisbursePayouts', {
                'pids': this.transactions.map((txn) => txn.paymentId)
            }).then(() => {
                this.disbursement.done = true;
            }).catch((error) => {
                this.disbursement.isDisburseError = true;
                if (error.response.data) {
                    this.disbursement.disburseMessage = error.response.data.message;
                } else {
                    this.disbursement.disburseMessage = 'Error saving disbursement status. Please try again.';
                }
            });
        },
        dismissConfirm () {
            this.$refs.popupModalConfirmation.hide();
        },
        getPaymentType (typeId) {
            switch (typeId) {
            case 1: return 'TeleConsultation';
            case 2: return 'Prescription';
            case 4: return 'Clinic Consultation';
            case 6:return 'Clinic Medicine Purchase';
            case 7: return 'User Vaccination';
            case 8 : return 'Company Vaccination';
            default: return 'Other';
            }
        },
        getPaymentTypeIdTitle (typeId) {
            switch (typeId) {
            case 1: return 'Consultation ID';
            case 2: return 'Prescription ID';
            case 4: return 'Consultation ID';
            case 6:return 'Prescription ID';
            default: return 'ID';
            }
        },
        dismissAndBackToList () {
            this.$refs.popupModalConfirmation.hide(() => {
                this.$router.push({
                    'name': 'admin-partner-payout-v2'
                });
            });
        }
    }
};

</script>
